import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  ArcElement,
  DoughnutController,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import {
  RefreshCw, Users, Activity, AlertTriangle, Clock, Server,
  Database, HardDrive, Search, Wifi, WifiOff,
  Monitor, Shield, Zap, Trash2, RotateCw, Play, Loader, CheckCircle, XCircle,
  Briefcase, Plus, Pencil
} from 'lucide-react';
import { useTheme } from '../utils/theme';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, LineController,
  BarElement, BarController, ArcElement, DoughnutController,
  Title, Tooltip, Legend, Filler
);

const AdminChart = ({ type, data, options }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new ChartJS(canvasRef.current, { type, data, options });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [type, data, options]);

  return <canvas ref={canvasRef} />;
};

const AUTO_REFRESH_MS = 30000;
/** Poll cache ops only while a clear/reload/prod-init is in flight */
const CACHE_OPS_POLL_MS = 10000;

const EMPTY_CAREER_ROLE_FORM = {
  title: '',
  location: '',
  type: 'Full-time',
  summary: '',
  active: true,
};

const DEFAULT_ROLE_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship'];

const cacheOpsSnapshotKey = (ops) => {
  const running = (ops?.running || [])
    .map((o) => `${o.id}:${o.type}:${o.name}:${o.status}`)
    .join('|');
  const history = (ops?.history || [])
    .slice(0, 10)
    .map((o) => `${o.id}:${o.status}:${o.endTime || ''}:${o.message || ''}`)
    .join('|');
  return `${running}::${history}`;
};

const opElapsedSec = (op) => {
  if (!op?.startTime) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(op.startTime).getTime()) / 1000));
};

import { authFetch } from '../utils/authFetch';

const adminFetch = (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return authFetch(url, {
    ...options,
    headers: { ...options.headers, 'x-user-email': user.email || '' },
  });
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [cacheData, setCacheData] = useState(null);
  const [cacheOps, setCacheOps] = useState({ running: [], history: [] });
  const [cacheActionInFlight, setCacheActionInFlight] = useState({});
  const [cacheActionError, setCacheActionError] = useState(null);
  const [cacheCancelInFlight, setCacheCancelInFlight] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userEdits, setUserEdits] = useState({});
  const [userSaving, setUserSaving] = useState({});
  const [memoryHistory, setMemoryHistory] = useState([]);
  const [clientHealth, setClientHealth] = useState({});
  const [careerRoles, setCareerRoles] = useState([]);
  const [careerRoleTypes, setCareerRoleTypes] = useState(DEFAULT_ROLE_TYPES);
  const [careerRolesLoading, setCareerRolesLoading] = useState(false);
  const [careerRolesError, setCareerRolesError] = useState(null);
  const [careerRoleForm, setCareerRoleForm] = useState({ ...EMPTY_CAREER_ROLE_FORM });
  const [careerRoleEditingId, setCareerRoleEditingId] = useState(null);
  const [careerRoleSaving, setCareerRoleSaving] = useState(false);
  const [careerRoleFormError, setCareerRoleFormError] = useState(null);
  const [careerRoleDeletingId, setCareerRoleDeletingId] = useState(null);

  useEffect(() => {
    const updateClientHealth = () => {
      const nav = window.navigator;
      const perf = window.performance;
      let lsUsage = 0;
      try {
        for (const key in localStorage) {
          if (localStorage.hasOwnProperty(key)) {
            lsUsage += (localStorage.getItem(key) || '').length * 2;
          }
        }
      } catch (e) { /* ignore */ }

      setClientHealth({
        online: navigator.onLine,
        userAgent: nav.userAgent,
        language: nav.language,
        cookiesEnabled: nav.cookieEnabled,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        devicePixelRatio: window.devicePixelRatio,
        localStorageUsageMB: +(lsUsage / 1048576).toFixed(2),
        localStorageKeys: Object.keys(localStorage).length,
        pageLoadTime: perf?.timing
          ? perf.timing.loadEventEnd - perf.timing.navigationStart
          : null,
        jsHeapMB: perf?.memory ? +(perf.memory.usedJSHeapSize / 1048576).toFixed(2) : null,
        jsHeapLimitMB: perf?.memory ? +(perf.memory.jsHeapSizeLimit / 1048576).toFixed(2) : null,
      });
    };
    updateClientHealth();
    window.addEventListener('online', updateClientHealth);
    window.addEventListener('offline', updateClientHealth);
    return () => {
      window.removeEventListener('online', updateClientHealth);
      window.removeEventListener('offline', updateClientHealth);
    };
  }, []);

  const fetchCacheOps = useCallback(async () => {
    try {
      const res = await adminFetch('/api/admin/cache/operations');
      const data = await res.json();
      setCacheOps((prev) => {
        if (cacheOpsSnapshotKey(prev) === cacheOpsSnapshotKey(data)) {
          return prev;
        }
        return data;
      });
      if (data.running && data.running.length === 0) {
        setCacheActionInFlight((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const key of Object.keys(next)) {
            if (next[key] === 'running') { next[key] = 'idle'; changed = true; }
          }
          return changed ? next : prev;
        });
      }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchAllData = useCallback(async () => {
    try {
      const [healthRes, statsRes, usersRes, cacheRes] = await Promise.all([
        adminFetch('/api/admin/health').then(r => r.json()),
        adminFetch('/api/admin/stats').then(r => r.json()),
        adminFetch('/api/admin/users').then(r => r.json()).catch(() => null),
        adminFetch('/api/admin/cache').then(r => r.json()),
      ]);

      setHealth(healthRes);
      setStats(statsRes);
      if (usersRes) setUsers(usersRes);
      setCacheData(cacheRes);
      setLastRefresh(new Date());
      fetchCacheOps();

      if (healthRes?.memory) {
        setMemoryHistory(prev => {
          const entry = {
            time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
            heapUsed: healthRes.memory.heapUsedMB,
            heapTotal: healthRes.memory.heapTotalMB,
            rss: healthRes.memory.rssMB,
          };
          return [...prev, entry].slice(-30);
        });
      }
    } catch (err) {
      console.error('Admin dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchCacheOps]);

  const handleCacheClear = async (name) => {
    if (cacheActionInFlight[name] === 'running') return;
    setCacheActionError(null);
    setCacheActionInFlight(prev => ({ ...prev, [name]: 'running' }));
    try {
      const res = await adminFetch(`/api/admin/cache/clear/${name}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.skipped) {
        setCacheActionInFlight(prev => ({ ...prev, [name]: 'idle' }));
        setCacheActionError(data.message || `Clear failed for ${name}`);
        return;
      }
      fetchCacheOps();
      setTimeout(() => { fetchAllData(); }, 2000);
    } catch (e) {
      setCacheActionInFlight(prev => ({ ...prev, [name]: 'idle' }));
      setCacheActionError(e.message || `Clear failed for ${name}`);
    }
  };

  const handleCacheReload = async (name) => {
    if (cacheActionInFlight[name] === 'running') return;
    setCacheActionError(null);
    setCacheActionInFlight(prev => ({ ...prev, [name]: 'running' }));
    try {
      const res = await adminFetch(`/api/admin/cache/reload/${name}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.skipped) {
        setCacheActionInFlight(prev => ({ ...prev, [name]: 'idle' }));
        setCacheActionError(data.message || `Reload failed for ${name}`);
        return;
      }
      fetchCacheOps();
      setTimeout(() => { fetchAllData(); }, 2000);
    } catch (e) {
      setCacheActionInFlight(prev => ({ ...prev, [name]: 'idle' }));
      setCacheActionError(e.message || `Reload failed for ${name}`);
    }
  };

  const handleProdInit = async () => {
    if (cacheActionInFlight['prod-init'] === 'running') return;
    setCacheActionError(null);
    setCacheActionInFlight(prev => ({ ...prev, 'prod-init': 'running' }));
    try {
      const res = await adminFetch('/api/admin/cache/prod-init', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.skipped) {
        setCacheActionInFlight(prev => ({ ...prev, 'prod-init': 'idle' }));
        setCacheActionError(data.message || 'Production init failed');
        return;
      }
      fetchCacheOps();
    } catch (e) {
      setCacheActionInFlight(prev => ({ ...prev, 'prod-init': 'idle' }));
      setCacheActionError(e.message || 'Production init failed');
    }
  };

  const handleCancelCacheJob = async () => {
    if (cacheCancelInFlight || !health?.cacheJobRunning) return;
    const label = health.cacheJobRunning;
    const phase = health.cacheJobPhase ? ` (phase: ${health.cacheJobPhase})` : '';
    if (!window.confirm(`Cancel cache job "${label}"${phase}? Manual reloads will be unblocked. In-flight work may continue briefly in the background.`)) {
      return;
    }
    setCacheActionError(null);
    setCacheCancelInFlight(true);
    try {
      const res = await adminFetch('/api/admin/cache/cancel-job', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setCacheActionError(data.message || 'Failed to cancel cache job');
        return;
      }
      fetchCacheOps();
      fetchAllData();
    } catch (e) {
      setCacheActionError(e.message || 'Failed to cancel cache job');
    } finally {
      setCacheCancelInFlight(false);
    }
  };

  const isCacheOpRunning = (name) => {
    return cacheOps.running?.some(op => op.name === name) || cacheActionInFlight[name] === 'running';
  };

  const isCacheJobBlocked = Boolean(health?.cacheJobRunning);

  const formatCacheJobElapsed = (ms) => {
    if (ms == null || ms < 0) return '--';
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
  };

  const handleUserFieldChange = (email, field, value) => {
    setUserEdits(prev => {
      const current = prev[email] || {};
      const original = users?.users?.find(u => u.email === email);
      const updated = { ...current, [field]: value };
      if (original && original[field] === value) {
        delete updated[field];
      }
      if (Object.keys(updated).length === 0) {
        const { [email]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [email]: updated };
    });
  };

  const handleSaveUser = async (email) => {
    const edits = userEdits[email];
    if (!edits || Object.keys(edits).length === 0) return;
    setUserSaving(prev => ({ ...prev, [email]: true }));
    try {
      const res = await adminFetch(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edits),
      });
      if (res.ok) {
        setUsers(prev => {
          if (!prev?.users) return prev;
          return {
            ...prev,
            users: prev.users.map(u => u.email === email ? { ...u, ...edits } : u),
          };
        });
        setUserEdits(prev => { const { [email]: _, ...rest } = prev; return rest; });
      }
    } catch (e) {
      console.error('Failed to save user:', e);
    } finally {
      setUserSaving(prev => ({ ...prev, [email]: false }));
    }
  };

  const fetchCareerRoles = useCallback(async () => {
    setCareerRolesLoading(true);
    setCareerRolesError(null);
    try {
      const res = await adminFetch('/api/admin/careers/roles');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch career roles');
      setCareerRoles(Array.isArray(data.roles) ? data.roles : []);
      if (Array.isArray(data.roleTypes) && data.roleTypes.length > 0) {
        setCareerRoleTypes(data.roleTypes);
      }
    } catch (err) {
      setCareerRolesError(err.message || 'Failed to fetch career roles');
    } finally {
      setCareerRolesLoading(false);
    }
  }, []);

  const resetCareerRoleForm = () => {
    setCareerRoleForm({ ...EMPTY_CAREER_ROLE_FORM });
    setCareerRoleEditingId(null);
    setCareerRoleFormError(null);
  };

  const handleCareerRoleFieldChange = (field, value) => {
    setCareerRoleForm((prev) => ({ ...prev, [field]: value }));
    setCareerRoleFormError(null);
  };

  const handleEditCareerRole = (role) => {
    setCareerRoleEditingId(role.id);
    setCareerRoleForm({
      title: role.title || '',
      location: role.location || '',
      type: role.type || 'Full-time',
      summary: role.summary || '',
      active: role.active !== false,
    });
    setCareerRoleFormError(null);
  };

  const handleSaveCareerRole = async (e) => {
    e.preventDefault();
    setCareerRoleSaving(true);
    setCareerRoleFormError(null);
    try {
      const payload = {
        title: careerRoleForm.title.trim(),
        location: careerRoleForm.location.trim(),
        type: careerRoleForm.type,
        summary: careerRoleForm.summary.trim(),
        active: careerRoleForm.active,
      };
      const url = careerRoleEditingId
        ? `/api/admin/careers/roles/${encodeURIComponent(careerRoleEditingId)}`
        : '/api/admin/careers/roles';
      const method = careerRoleEditingId ? 'PUT' : 'POST';
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save role');
      resetCareerRoleForm();
      await fetchCareerRoles();
    } catch (err) {
      setCareerRoleFormError(err.message || 'Failed to save role');
    } finally {
      setCareerRoleSaving(false);
    }
  };

  const handleDeleteCareerRole = async (role) => {
    if (!window.confirm(`Delete "${role.title}"? This cannot be undone.`)) return;
    setCareerRoleDeletingId(role.id);
    setCareerRolesError(null);
    try {
      const res = await adminFetch(`/api/admin/careers/roles/${encodeURIComponent(role.id)}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete role');
      if (careerRoleEditingId === role.id) resetCareerRoleForm();
      await fetchCareerRoles();
    } catch (err) {
      setCareerRolesError(err.message || 'Failed to delete role');
    } finally {
      setCareerRoleDeletingId(null);
    }
  };

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  useEffect(() => {
    if (activeTab === 'careers') fetchCareerRoles();
  }, [activeTab, fetchCareerRoles]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchAllData, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchAllData]);

  const cacheOpsRunningCount = cacheOps.running?.length ?? 0;
  const cacheActionRunning = Object.values(cacheActionInFlight).some((v) => v === 'running');

  // Local 1s tick for elapsed labels only — no API calls
  const [, setOpsElapsedTick] = useState(0);
  useEffect(() => {
    if (cacheOpsRunningCount === 0) return undefined;
    const id = setInterval(() => setOpsElapsedTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [cacheOpsRunningCount]);

  useEffect(() => {
    if (!cacheActionRunning && cacheOpsRunningCount === 0) return undefined;
    fetchCacheOps();
    const id = setInterval(fetchCacheOps, CACHE_OPS_POLL_MS);
    return () => clearInterval(id);
  }, [cacheActionRunning, cacheOpsRunningCount, fetchCacheOps]);

  const formatUptime = (sec) => {
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };

  const timeAgo = (iso) => {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const formatMs = (ms) => {
    if (ms == null) return '--';
    const totalSec = Math.floor(ms / 1000);
    if (totalSec < 60) return `${totalSec}s`;
    const mins = Math.floor(totalSec / 60);
    if (mins < 60) return `${mins}m ${totalSec % 60}s`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  const ttlPct = (remaining, total) => {
    if (!remaining || !total) return 0;
    return Math.max(0, Math.min(100, (remaining / total) * 100));
  };

  const ttlColor = (remaining, total) => {
    if (!remaining || !total) return '#9ca3af';
    const pct = (remaining / total) * 100;
    if (pct > 50) return '#22c55e';
    if (pct > 20) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusColor = (status) => {
    if (status < 300) return '#22c55e';
    if (status < 400) return '#3b82f6';
    if (status < 500) return '#f59e0b';
    return '#ef4444';
  };

  // ==================== Chart Data ====================

  // Charts don't inherit CSS theme colors, so remap line/label/grid
  // colors when dark mode is active (navy lines vanish on dark panels).
  const { mode: themeMode } = useTheme();
  const isDarkTheme = themeMode === 'dark';

  const requestVolumeData = useMemo(() => {
    if (!stats?.hourlyRequests) return null;
    const sorted = Object.entries(stats.hourlyRequests)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-24);
    if (sorted.length === 0) return null;
    return {
      labels: sorted.map(([h]) => h.slice(11) + ':00'),
      datasets: [
        {
          label: 'Requests',
          data: sorted.map(([, v]) => v.count),
          borderColor: isDarkTheme ? '#38b6e6' : '#0B2942',
          backgroundColor: 'rgba(9,146,194,0.15)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'Errors',
          data: sorted.map(([, v]) => v.errors),
          borderColor: isDarkTheme ? '#f87171' : '#ef4444',
          backgroundColor: 'rgba(239,68,68,0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
        }
      ]
    };
  }, [stats, isDarkTheme]);

  const statusCodeData = useMemo(() => {
    if (!stats?.statusCodes || Object.keys(stats.statusCodes).length === 0) return null;
    const grouped = { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0 };
    for (const [code, count] of Object.entries(stats.statusCodes)) {
      const cat = Math.floor(parseInt(code) / 100) + 'xx';
      if (grouped[cat] !== undefined) grouped[cat] += count;
    }
    const colorMap = { '2xx': '#22c55e', '3xx': '#3b82f6', '4xx': '#f59e0b', '5xx': '#ef4444' };
    const entries = Object.entries(grouped).filter(([, v]) => v > 0);
    if (entries.length === 0) return null;
    return {
      labels: entries.map(([k]) => k),
      datasets: [{
        data: entries.map(([, v]) => v),
        backgroundColor: entries.map(([k]) => colorMap[k] || '#6b7280'),
        borderWidth: 0,
      }]
    };
  }, [stats]);

  const topEndpointsData = useMemo(() => {
    if (!stats?.endpoints || stats.endpoints.length === 0) return null;
    const top = stats.endpoints.slice(0, 10);
    return {
      labels: top.map(e => e.path.length > 40 ? e.path.slice(0, 40) + '...' : e.path),
      datasets: [{
        label: 'Requests',
        data: top.map(e => e.count),
        backgroundColor: isDarkTheme ? '#38b6e6' : '#011627',
        borderRadius: 4,
      }]
    };
  }, [stats, isDarkTheme]);

  const memoryChartData = useMemo(() => {
    if (memoryHistory.length < 2) return null;
    return {
      labels: memoryHistory.map(e => e.time),
      datasets: [
        {
          label: 'RSS',
          data: memoryHistory.map(e => e.rss),
          borderColor: isDarkTheme ? '#eaf0f8' : '#011627',
          backgroundColor: 'rgba(11,45,114,0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
        },
        {
          label: 'Heap Total',
          data: memoryHistory.map(e => e.heapTotal),
          borderColor: isDarkTheme ? '#38b6e6' : '#0B2942',
          backgroundColor: 'rgba(9,146,194,0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
        },
        {
          label: 'Heap Used',
          data: memoryHistory.map(e => e.heapUsed),
          borderColor: isDarkTheme ? '#34d399' : '#22c55e',
          backgroundColor: 'rgba(34,197,94,0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
        }
      ]
    };
  }, [memoryHistory, isDarkTheme]);

  const chartTickColor = isDarkTheme ? '#93a2b8' : '#666';
  const chartGridColor = isDarkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, font: { size: 11 }, color: chartTickColor } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, color: chartTickColor } },
      y: { beginAtZero: true, grid: { color: chartGridColor }, ticks: { font: { size: 10 }, color: chartTickColor } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, font: { size: 11 }, color: chartTickColor } },
    },
    cutout: '60%',
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: chartGridColor }, ticks: { font: { size: 10 }, color: chartTickColor } },
      y: { grid: { display: false }, ticks: { font: { size: 9 }, color: chartTickColor } },
    },
  };

  // ==================== Filtered Users ====================

  const filteredUsers = useMemo(() => {
    if (!users?.users) return [];
    if (!userSearch) return users.users;
    const q = userSearch.toLowerCase();
    return users.users.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const activeUsersLastHour = useMemo(() => {
    if (!users?.users) return 0;
    const cutoff = Date.now() - 3600000;
    return users.users.filter(u => u.last_activity && new Date(u.last_activity).getTime() > cutoff).length;
  }, [users]);

  const activeUsersLastDay = useMemo(() => {
    if (!users?.users) return 0;
    const cutoff = Date.now() - 86400000;
    return users.users.filter(u => u.last_activity && new Date(u.last_activity).getTime() > cutoff).length;
  }, [users]);

  // ==================== Render ====================

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-left">
          <Shield size={24} color="#011627" />
          <h2>Admin Dashboard</h2>
          <span className={`admin-status-badge ${health?.status === 'healthy' ? 'healthy' : 'unhealthy'}`}>
            {health?.status || 'unknown'}
          </span>
        </div>
        <div className="admin-header-right">
          <label className="admin-auto-refresh-label">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button onClick={fetchAllData} className="admin-refresh-btn" title="Refresh now">
            <RefreshCw size={16} />
          </button>
          {lastRefresh && <span className="admin-last-refresh">Updated {lastRefresh.toLocaleTimeString()}</span>}
        </div>
      </div>

      <div className="admin-tabs">
        {['overview', 'users', 'careers', 'system'].map(tab => (
          <button
            key={tab}
            className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && <Activity size={16} />}
            {tab === 'users' && <Users size={16} />}
            {tab === 'careers' && <Briefcase size={16} />}
            {tab === 'system' && <Server size={16} />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === 'overview' && (
        <div className="admin-content">
          <div className="admin-cards">
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: '#e0f2fe' }}>
                <Clock size={20} color="#0B2942" />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Uptime</span>
                <span className="admin-card-value">{health ? formatUptime(health.uptime) : '--'}</span>
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: '#e8f5e9' }}>
                <Users size={20} color="#22c55e" />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Total Users</span>
                <span className="admin-card-value">{users?.totalUsers ?? '--'}</span>
                <span className="admin-card-sub">{activeUsersLastHour} active (1h) / {activeUsersLastDay} active (24h)</span>
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: '#ede9fe' }}>
                <Zap size={20} color="#7c3aed" />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Total Requests</span>
                <span className="admin-card-value">{stats?.totalRequests?.toLocaleString() ?? '--'}</span>
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: stats?.errorRate > 5 ? '#fee2e2' : '#fef3c7' }}>
                <AlertTriangle size={20} color={stats?.errorRate > 5 ? '#ef4444' : '#f59e0b'} />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Error Rate</span>
                <span className="admin-card-value" style={{ color: stats?.errorRate > 5 ? '#ef4444' : 'inherit' }}>
                  {stats?.errorRate != null ? stats.errorRate + '%' : '--'}
                </span>
                <span className="admin-card-sub">{stats?.totalErrors ?? 0} total errors</span>
              </div>
            </div>
          </div>

          <div className="admin-charts-row">
            <div className="admin-chart-panel admin-chart-wide">
              <h3>Request Volume (Last 24h)</h3>
              <div className="admin-chart-container">
                {requestVolumeData
                  ? <AdminChart type="line" data={requestVolumeData} options={chartOptions} />
                  : <p className="admin-no-data">No request data yet. Data populates as the server handles API requests.</p>}
              </div>
            </div>
            <div className="admin-chart-panel admin-chart-narrow">
              <h3>Response Status Codes</h3>
              <div className="admin-chart-container">
                {statusCodeData
                  ? <AdminChart type="doughnut" data={statusCodeData} options={doughnutOptions} />
                  : <p className="admin-no-data">No data yet</p>}
              </div>
            </div>
          </div>

          <div className="admin-charts-row">
            <div className="admin-chart-panel admin-chart-wide">
              <h3>Top Endpoints by Request Count</h3>
              <div className="admin-chart-container admin-chart-tall">
                {topEndpointsData
                  ? <AdminChart type="bar" data={topEndpointsData} options={barOptions} />
                  : <p className="admin-no-data">No data yet</p>}
              </div>
            </div>
            <div className="admin-chart-panel admin-chart-narrow">
              <h3>Memory Usage (MB)</h3>
              <div className="admin-chart-container">
                {memoryChartData
                  ? <AdminChart type="line" data={memoryChartData} options={chartOptions} />
                  : <p className="admin-no-data">Collecting data... (updates every 30s)</p>}
              </div>
              {health?.memory && (
                <div className="admin-memory-stats">
                  <div><strong>Heap:</strong> {health.memory.heapUsedMB} / {health.memory.heapTotalMB} MB ({health.memory.heapUsagePct}%)</div>
                  <div><strong>RSS:</strong> {health.memory.rssMB} MB</div>
                  <div className="admin-memory-bar">
                    <div
                      className="admin-memory-bar-fill"
                      style={{
                        width: `${Math.min(health.memory.heapUsagePct, 100)}%`,
                        background: health.memory.heapUsagePct > 85 ? '#ef4444' : health.memory.heapUsagePct > 70 ? '#f59e0b' : '#22c55e'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="admin-section">
            <h3>Endpoint Performance</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Requests</th>
                    <th>Errors</th>
                    <th>Avg Time</th>
                    <th>Max Time</th>
                    <th>Last Access</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.endpoints || []).slice(0, 25).map((ep, i) => (
                    <tr key={i}>
                      <td className="admin-endpoint-path">{ep.path}</td>
                      <td>{ep.count.toLocaleString()}</td>
                      <td style={{ color: ep.errors > 0 ? '#ef4444' : 'inherit' }}>{ep.errors}</td>
                      <td>{ep.avgTime}ms</td>
                      <td style={{ color: ep.maxTime > 5000 ? '#ef4444' : ep.maxTime > 2000 ? '#f59e0b' : 'inherit' }}>
                        {ep.maxTime}ms
                      </td>
                      <td>{timeAgo(ep.lastAccess)}</td>
                    </tr>
                  ))}
                  {(!stats?.endpoints || stats.endpoints.length === 0) && (
                    <tr><td colSpan="6" className="admin-no-data">No endpoint data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {(stats?.totalErrors || 0) > 0 && (
            <div className="admin-section">
              <h3><AlertTriangle size={16} color="#ef4444" /> Recent Errors</h3>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Method</th>
                      <th>Path</th>
                      <th>Status</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.recentRequests || [])
                      .filter(r => r.status >= 400)
                      .slice(0, 20)
                      .map((r, i) => (
                        <tr key={i}>
                          <td>{new Date(r.timestamp).toLocaleString()}</td>
                          <td><span className="admin-method-badge">{r.method}</span></td>
                          <td className="admin-endpoint-path">{r.path}</td>
                          <td><span className="admin-status-code" style={{ background: getStatusColor(r.status) }}>{r.status}</span></td>
                          <td>{r.duration}ms</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="admin-section">
            <h3>Recent Requests</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Method</th>
                    <th>Path</th>
                    <th>Status</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.recentRequests || []).slice(0, 30).map((r, i) => (
                    <tr key={i}>
                      <td>{new Date(r.timestamp).toLocaleString()}</td>
                      <td><span className="admin-method-badge">{r.method}</span></td>
                      <td className="admin-endpoint-path">{r.path}</td>
                      <td>
                        <span className="admin-status-code" style={{ background: getStatusColor(r.status) }}>
                          {r.status}
                        </span>
                      </td>
                      <td>{r.duration}ms</td>
                    </tr>
                  ))}
                  {(!stats?.recentRequests || stats.recentRequests.length === 0) && (
                    <tr><td colSpan="5" className="admin-no-data">No requests yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== USERS TAB ==================== */}
      {activeTab === 'users' && (
        <div className="admin-content">
          <div className="admin-cards" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: '#e8f5e9' }}>
                <Users size={20} color="#22c55e" />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Total Users</span>
                <span className="admin-card-value">{users?.totalUsers ?? '--'}</span>
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: '#e0f2fe' }}>
                <Activity size={20} color="#0B2942" />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Active Last Hour</span>
                <span className="admin-card-value">{activeUsersLastHour}</span>
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-icon" style={{ background: '#ede9fe' }}>
                <Clock size={20} color="#7c3aed" />
              </div>
              <div className="admin-card-info">
                <span className="admin-card-label">Active Last 24h</span>
                <span className="admin-card-value">{activeUsersLastDay}</span>
              </div>
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-section-header">
              <h3><Users size={16} /> User Management</h3>
              <div className="admin-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table admin-users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Tier</th>
                    <th>Plan</th>
                    <th>Joined</th>
                    <th>Last Activity</th>
                    <th>Portfolio</th>
                    <th>Watchlist</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, i) => {
                    const edits = userEdits[u.email] || {};
                    const hasEdits = Object.keys(edits).length > 0;
                    const saving = userSaving[u.email];
                    return (
                    <tr key={i} className={hasEdits ? 'admin-row-edited' : ''}>
                      <td>{u.email}</td>
                      <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '--'}</td>
                      <td><span className={`admin-badge ${u.role === 'admin' ? 'purple' : 'grey'}`}>{u.role || 'user'}</span></td>
                      <td>
                        <select
                          className={`admin-inline-select status-${edits.status || u.status || 'active'}`}
                          value={edits.status ?? u.status ?? 'active'}
                          onChange={e => handleUserFieldChange(u.email, 'status', e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                          <option value="deleted">Deleted</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="admin-inline-select"
                          value={edits.tier ?? u.tier ?? 'basic'}
                          onChange={e => handleUserFieldChange(u.email, 'tier', e.target.value)}
                        >
                          <option value="basic">Basic</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="admin-inline-select"
                          value={edits.plan ?? u.plan ?? 'free'}
                          onChange={e => handleUserFieldChange(u.email, 'plan', e.target.value)}
                        >
                          <option value="free">Free</option>
                          <option value="premium">Premium</option>
                        </select>
                      </td>
                      <td>{u.create_date ? new Date(u.create_date).toLocaleDateString() : '--'}</td>
                      <td>
                        <span className={`admin-activity-badge ${u.last_activity && (Date.now() - new Date(u.last_activity).getTime()) < 3600000 ? 'recent' : ''}`}>
                          {timeAgo(u.last_activity)}
                        </span>
                      </td>
                      <td>
                        {u.hasPortfolio
                          ? <span className="admin-badge green">{u.portfolioCount} stocks</span>
                          : <span className="admin-badge grey">None</span>}
                      </td>
                      <td>
                        {u.hasWatchlist
                          ? <span className="admin-badge blue">{u.watchlistCount} items</span>
                          : <span className="admin-badge grey">None</span>}
                      </td>
                      <td>
                        {hasEdits && (
                          <button
                            className="admin-save-user-btn"
                            onClick={() => handleSaveUser(u.email)}
                            disabled={saving}
                            title="Save changes"
                          >
                            {saving ? <Loader size={14} className="spin" /> : <CheckCircle size={14} />}
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan="11" className="admin-no-data">No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CAREERS TAB ==================== */}
      {activeTab === 'careers' && (
        <div className="admin-content">
          <div className="admin-section">
            <div className="admin-section-header">
              <h3><Briefcase size={16} /> Open Roles — Careers Page</h3>
              <button type="button" className="admin-refresh-btn" onClick={fetchCareerRoles} title="Refresh roles">
                <RefreshCw size={16} className={careerRolesLoading ? 'spin' : ''} />
              </button>
            </div>
            <p className="admin-careers-intro">
              Add, edit, or remove job listings shown on the public Careers page. Inactive roles are hidden from visitors.
            </p>

            <form className="admin-careers-form" onSubmit={handleSaveCareerRole}>
              <h4>{careerRoleEditingId ? 'Edit Role' : 'Add New Role'}</h4>
              {careerRoleFormError && (
                <div className="admin-cache-action-error" role="alert">{careerRoleFormError}</div>
              )}
              <div className="admin-careers-form-grid">
                <label className="admin-careers-field">
                  <span>Title</span>
                  <input
                    type="text"
                    value={careerRoleForm.title}
                    onChange={(e) => handleCareerRoleFieldChange('title', e.target.value)}
                    placeholder="e.g. Full-Stack Engineer"
                    maxLength={120}
                    required
                  />
                </label>
                <label className="admin-careers-field">
                  <span>Location</span>
                  <input
                    type="text"
                    value={careerRoleForm.location}
                    onChange={(e) => handleCareerRoleFieldChange('location', e.target.value)}
                    placeholder="e.g. Remote (North America)"
                    maxLength={120}
                    required
                  />
                </label>
                <label className="admin-careers-field">
                  <span>Type</span>
                  <select
                    value={careerRoleForm.type}
                    onChange={(e) => handleCareerRoleFieldChange('type', e.target.value)}
                    required
                  >
                    {careerRoleTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-careers-field admin-careers-field--checkbox">
                  <input
                    type="checkbox"
                    checked={careerRoleForm.active}
                    onChange={(e) => handleCareerRoleFieldChange('active', e.target.checked)}
                  />
                  <span>Visible on Careers page</span>
                </label>
              </div>
              <label className="admin-careers-field admin-careers-field--full">
                <span>Summary</span>
                <textarea
                  value={careerRoleForm.summary}
                  onChange={(e) => handleCareerRoleFieldChange('summary', e.target.value)}
                  placeholder="Brief description of the role and what the candidate will work on."
                  rows={4}
                  maxLength={2000}
                  required
                />
              </label>
              <div className="admin-careers-form-actions">
                <button type="submit" className="admin-save-user-btn" disabled={careerRoleSaving}>
                  {careerRoleSaving
                    ? <Loader size={14} className="spin" />
                    : (careerRoleEditingId ? <Pencil size={14} /> : <Plus size={14} />)}
                  {careerRoleEditingId ? 'Update Role' : 'Add Role'}
                </button>
                {careerRoleEditingId && (
                  <button type="button" className="admin-careers-cancel-btn" onClick={resetCareerRoleForm} disabled={careerRoleSaving}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="admin-section">
            <div className="admin-section-header">
              <h3>Current Roles ({careerRoles.length})</h3>
            </div>
            {careerRolesError && (
              <div className="admin-cache-action-error" role="alert">{careerRolesError}</div>
            )}
            {careerRolesLoading && careerRoles.length === 0 ? (
              <p className="admin-no-data">Loading roles…</p>
            ) : careerRoles.length === 0 ? (
              <p className="admin-no-data">No open roles yet. The Careers page will show a message that there are no current openings.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table admin-careers-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Location</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th>Summary</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {careerRoles.map((role) => (
                      <tr key={role.id} className={careerRoleEditingId === role.id ? 'admin-row-edited' : ''}>
                        <td>{role.title}</td>
                        <td>{role.location}</td>
                        <td>{role.type}</td>
                        <td>
                          <span className={`admin-badge ${role.active !== false ? 'green' : 'grey'}`}>
                            {role.active !== false ? 'Active' : 'Hidden'}
                          </span>
                        </td>
                        <td>{role.updatedAt ? new Date(role.updatedAt).toLocaleDateString() : '—'}</td>
                        <td className="admin-careers-summary-cell">{role.summary}</td>
                        <td className="admin-careers-actions-cell">
                          <button
                            type="button"
                            className="admin-careers-icon-btn"
                            onClick={() => handleEditCareerRole(role)}
                            title="Edit role"
                            aria-label={`Edit ${role.title}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            className="admin-careers-icon-btn admin-careers-icon-btn--danger"
                            onClick={() => handleDeleteCareerRole(role)}
                            disabled={careerRoleDeletingId === role.id}
                            title="Delete role"
                            aria-label={`Delete ${role.title}`}
                          >
                            {careerRoleDeletingId === role.id
                              ? <Loader size={14} className="spin" />
                              : <Trash2 size={14} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== SYSTEM TAB ==================== */}
      {activeTab === 'system' && (
        <div className="admin-content">
          <div className="admin-section">
            <h3><Server size={16} /> Server Information</h3>
            <div className="admin-info-grid">
              <div className="admin-info-item">
                <span className="admin-info-label">Node.js</span>
                <span className="admin-info-value">{health?.nodeVersion || '--'}</span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">Platform</span>
                <span className="admin-info-value">{health?.platform || '--'}</span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">PID</span>
                <span className="admin-info-value">{health?.pid || '--'}</span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">Environment</span>
                <span className="admin-info-value">
                  <span className={`admin-env-badge ${health?.env}`}>{health?.env || '--'}</span>
                </span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">Uptime</span>
                <span className="admin-info-value">{health ? formatUptime(health.uptime) : '--'}</span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">Started</span>
                <span className="admin-info-value">{health?.startTime ? new Date(health.startTime).toLocaleString() : '--'}</span>
              </div>
            </div>
          </div>

          <div className="admin-section">
            <h3><HardDrive size={16} /> Memory Details</h3>
            <div className="admin-memory-grid">
              <div className="admin-memory-card">
                <span className="admin-memory-card-label">RSS (Total)</span>
                <span className="admin-memory-card-value">{health?.memory?.rssMB || '--'} MB</span>
              </div>
              <div className="admin-memory-card">
                <span className="admin-memory-card-label">Heap Total</span>
                <span className="admin-memory-card-value">{health?.memory?.heapTotalMB || '--'} MB</span>
              </div>
              <div className="admin-memory-card">
                <span className="admin-memory-card-label">Heap Used</span>
                <span className="admin-memory-card-value">{health?.memory?.heapUsedMB || '--'} MB</span>
              </div>
              <div className="admin-memory-card">
                <span className="admin-memory-card-label">Heap Usage</span>
                <span className="admin-memory-card-value" style={{ color: (health?.memory?.heapUsagePct || 0) > 85 ? '#ef4444' : 'inherit' }}>
                  {health?.memory?.heapUsagePct || '--'}%
                </span>
                <div className="admin-memory-bar">
                  <div
                    className="admin-memory-bar-fill"
                    style={{
                      width: `${Math.min(health?.memory?.heapUsagePct || 0, 100)}%`,
                      background: (health?.memory?.heapUsagePct || 0) > 85 ? '#ef4444'
                        : (health?.memory?.heapUsagePct || 0) > 70 ? '#f59e0b' : '#22c55e'
                    }}
                  />
                </div>
              </div>
            </div>
            {memoryChartData && (
              <div className="admin-chart-panel" style={{ marginTop: '1rem', boxShadow: 'none', border: 'none', padding: '0.5rem 0' }}>
                <h4>Memory Over Time (MB)</h4>
                <div className="admin-chart-container">
                  <AdminChart type="line" data={memoryChartData} options={chartOptions} />
                </div>
              </div>
            )}
          </div>

          <div className="admin-section">
            <h3><Monitor size={16} /> Client-Side Health</h3>
            <div className="admin-info-grid">
              <div className="admin-info-item">
                <span className="admin-info-label">Connection</span>
                <span className="admin-info-value">
                  {clientHealth.online
                    ? <><Wifi size={14} color="#22c55e" /> Online</>
                    : <><WifiOff size={14} color="#ef4444" /> Offline</>}
                </span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">Screen</span>
                <span className="admin-info-value">{clientHealth.screenWidth}x{clientHealth.screenHeight} @{clientHealth.devicePixelRatio}x</span>
              </div>
              <div className="admin-info-item">
                <span className="admin-info-label">LocalStorage</span>
                <span className="admin-info-value">{clientHealth.localStorageUsageMB} MB ({clientHealth.localStorageKeys} keys)</span>
              </div>
              {clientHealth.jsHeapMB != null && (
                <div className="admin-info-item">
                  <span className="admin-info-label">JS Heap</span>
                  <span className="admin-info-value">{clientHealth.jsHeapMB} / {clientHealth.jsHeapLimitMB} MB</span>
                </div>
              )}
              {clientHealth.pageLoadTime > 0 && (
                <div className="admin-info-item">
                  <span className="admin-info-label">Page Load</span>
                  <span className="admin-info-value">{clientHealth.pageLoadTime}ms</span>
                </div>
              )}
              <div className="admin-info-item">
                <span className="admin-info-label">Language</span>
                <span className="admin-info-value">{clientHealth.language}</span>
              </div>
            </div>
          </div>

          <div className="admin-section">
            <div className="admin-section-header">
              <h3><Database size={16} /> Cache Management</h3>
              <div className="admin-cache-actions-top">
                <button
                  className={`admin-prod-init-btn ${isCacheOpRunning('all') || isCacheJobBlocked ? 'running' : ''}`}
                  onClick={handleProdInit}
                  disabled={isCacheOpRunning('all') || isCacheJobBlocked}
                  title="Clear all caches and reload everything (full production init)"
                >
                  {isCacheOpRunning('all')
                    ? <><Loader size={14} className="admin-spin" /> Running Prod Init...</>
                    : isCacheJobBlocked
                      ? <><Loader size={14} className="admin-spin" /> {health.cacheJobRunning} running...</>
                      : <><Play size={14} /> Run Prod Init</>}
                </button>
                {isCacheJobBlocked && (
                  <button
                    type="button"
                    className="admin-cache-cancel-job-btn"
                    onClick={handleCancelCacheJob}
                    disabled={cacheCancelInFlight}
                    title="Emergency: release cache mutex so manual reloads work again"
                  >
                    {cacheCancelInFlight
                      ? <><Loader size={14} className="admin-spin" /> Cancelling...</>
                      : <><XCircle size={14} /> Cancel Cache Job</>}
                  </button>
                )}
              </div>
            </div>

            {cacheData && (
              <div className="admin-cache-total">
                Total cache size: <strong>{cacheData.totalSizeMB} MB</strong>
              </div>
            )}

            {cacheActionError && (
              <div className="admin-cache-action-error" role="alert">
                {cacheActionError}
              </div>
            )}

            {isCacheJobBlocked && (
              <div className="admin-cache-action-error" role="status">
                Background cache job <strong>{health.cacheJobRunning}</strong>
                {health.cacheJobPhase ? <> — phase: <strong>{health.cacheJobPhase}</strong></> : null}
                {health.cacheJobElapsedMs != null ? <> ({formatCacheJobElapsed(health.cacheJobElapsedMs)} elapsed)</> : null}
                . Manual clear/reload is blocked until it finishes or you use <strong>Cancel Cache Job</strong>.
              </div>
            )}

            <div className="admin-cache-mgmt-grid">
              {(cacheData?.caches || []).map((c, i) => {
                const running = isCacheOpRunning(c.name);
                const blocked = isCacheJobBlocked && !running;
                const runningOp = cacheOps.running?.find(op => op.name === c.name);
                return (
                  <div key={i} className={`admin-cache-mgmt-card ${c.exists ? '' : 'missing'} ${running ? 'busy' : ''} ${c.expired ? 'expired' : ''}`}>
                    <div className="admin-cache-mgmt-header">
                      <Database size={16} color={c.exists ? '#0B2942' : '#9ca3af'} />
                      <span className="admin-cache-name">{c.name}</span>
                      <span className={`admin-cache-status ${c.expired ? 'expired' : c.exists ? 'active' : 'inactive'}`}>
                        {c.expired ? 'Expired' : c.exists ? 'Active' : 'Missing'}
                      </span>
                    </div>
                    <div className="admin-cache-mgmt-size">{c.sizeMB} MB</div>

                    <div className="admin-cache-ttl-section">
                      {c.ttlMs != null ? (
                        <>
                          <div className="admin-cache-ttl-row">
                            <span className="admin-cache-ttl-label">TTL</span>
                            <span className="admin-cache-ttl-value">{formatMs(c.ttlMs)}</span>
                          </div>
                          <div className="admin-cache-ttl-row">
                            <span className="admin-cache-ttl-label">Updated</span>
                            <span className="admin-cache-ttl-value">
                              {c.lastUpdated ? timeAgo(c.lastUpdated) : 'Never'}
                            </span>
                          </div>
                          <div className="admin-cache-ttl-row">
                            <span className="admin-cache-ttl-label">Remaining</span>
                            <span className="admin-cache-ttl-value" style={{ color: ttlColor(c.remainingMs, c.ttlMs), fontWeight: 600 }}>
                              {c.remainingMs != null ? (c.remainingMs === 0 ? 'Expired' : formatMs(c.remainingMs)) : '--'}
                            </span>
                          </div>
                          <div className="admin-cache-ttl-bar">
                            <div
                              className="admin-cache-ttl-bar-fill"
                              style={{
                                width: `${ttlPct(c.remainingMs, c.ttlMs)}%`,
                                background: ttlColor(c.remainingMs, c.ttlMs),
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="admin-cache-ttl-row">
                          <span className="admin-cache-ttl-label">TTL</span>
                          <span className="admin-cache-ttl-value" style={{ color: '#9ca3af' }}>Manual</span>
                        </div>
                      )}
                    </div>

                    {running && runningOp && (
                      <div className="admin-cache-mgmt-progress">
                        <Loader size={12} className="admin-spin" />
                        <span>{runningOp.type === 'clear' ? 'Clearing' : 'Reloading'}... {opElapsedSec(runningOp)}s</span>
                      </div>
                    )}
                    <div className="admin-cache-mgmt-buttons">
                      <button
                        className="admin-cache-btn admin-cache-btn-clear"
                        onClick={() => handleCacheClear(c.name)}
                        disabled={running || blocked}
                        title={blocked ? `Blocked: ${health.cacheJobRunning} is running` : `Clear ${c.name} cache`}
                      >
                        <Trash2 size={13} /> Clear
                      </button>
                      <button
                        className="admin-cache-btn admin-cache-btn-reload"
                        onClick={() => handleCacheReload(c.name)}
                        disabled={running || blocked}
                        title={blocked ? `Blocked: ${health.cacheJobRunning} is running` : `Reload ${c.name} cache from source`}
                      >
                        <RotateCw size={13} /> Reload
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(cacheOps.running?.length > 0 || cacheOps.history?.length > 0) && (
            <div className="admin-section">
              <h3><Activity size={16} /> Cache Operations</h3>

              {cacheOps.running?.length > 0 && (
                <>
                  <h4 className="admin-cache-ops-subhead">Running</h4>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Cache</th>
                          <th>Operation</th>
                          <th>Started</th>
                          <th>Elapsed</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cacheOps.running.map((op, i) => (
                          <tr key={i}>
                            <td><strong>{op.name}</strong></td>
                            <td><span className="admin-cache-op-type">{op.type}</span></td>
                            <td>{new Date(op.startTime).toLocaleTimeString()}</td>
                            <td>{opElapsedSec(op)}s</td>
                            <td><span className="admin-cache-op-running"><Loader size={12} className="admin-spin" /> Running</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {cacheOps.history?.length > 0 && (
                <>
                  <h4 className="admin-cache-ops-subhead">Recent History</h4>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Cache</th>
                          <th>Operation</th>
                          <th>Time</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cacheOps.history.slice(0, 15).map((op, i) => (
                          <tr key={i}>
                            <td><strong>{op.name}</strong></td>
                            <td><span className="admin-cache-op-type">{op.type}</span></td>
                            <td>{new Date(op.endTime).toLocaleTimeString()}</td>
                            <td>{op.durationMs < 1000 ? op.durationMs + 'ms' : Math.round(op.durationMs / 1000) + 's'}</td>
                            <td>
                              {op.status === 'completed'
                                ? <span className="admin-cache-op-ok"><CheckCircle size={13} /> Done</span>
                                : <span className="admin-cache-op-fail"><XCircle size={13} /> Failed</span>}
                            </td>
                            <td className="admin-cache-op-msg">{op.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
